import { useState, useContext, useCallback, useEffect, memo } from 'react';
import { useParams } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import debounce from 'lodash.debounce';
import AppContext from '../AppContext/AppContext';
import Modal from '../Modal/Modal';
import Panel from '../Panel/Panel';
import Iconify from '../Iconify/Iconify';
import { IconButton, Tooltip, Button, Stack } from '@mui/material';

function Tasks() {
  const { projectId } = useParams();
  const { paged = 1 } = useParams();
  const [numberOfPages, setNumberOfPages] = useState(1);
  const [openModal, setOpenModal] = useState(false);
  const [openModalConfirm, setOpenModalConfirm] = useState(false);
  const [taskDeleteId, setTaskDeleteId] = useState('');
  const [taskDelete, setTaskDelete] = useState<any>({});
  const { state, dispatch } = useContext(AppContext);
  const { tasks } = state;
  const [taskEdited, setTaskEdited] = useState({ _id: '', name: '' });
  const [exportJson, setExportJson] = useState('');
  const [project, setProject] = useState<any>({});
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const getAllTasks = useCallback(async () => {
    try {
      const option = {
        method: 'GET',
        url: '/api/v1/tasks/project/' + projectId + '?page=' + paged,
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      };
      const res = await axios(option);
      if (res.data.data.tasks) {
        const newTasks = res.data.data.tasks.filter((task: any) => !task.deleted);
        const tasksRemaining = res.data.data.tasksRemaining;
        dispatch({ type: 'GET_ALL_TASKS', payload: newTasks });
        dispatch({ type: 'GET_TASKS_REMAINING', payload: tasksRemaining });
        setProject(res.data.data.project);
        setNumberOfPages(res.data.data.numberOfPages);
        setLoading(false);
      }
    } catch (error) {
      console.log(error);
    }
  }, [dispatch, paged]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      const taskProcessing = tasks.find((task: any) => task.status === 'processing');
      if (taskProcessing) {
        getAllTasks();
      }
    }, 10000);

    return () => clearInterval(intervalId);
  }, [tasks]);

  useEffect(() => {
    getAllTasks();
  }, [getAllTasks]);

  const updateTask = useCallback(
    debounce(async () => {
      if (taskEdited._id === '') return;
      try {
        const option = {
          method: 'PUT',
          url: '/api/v1/tasks/' + taskEdited._id,
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
          data: taskEdited,
        };
        await axios(option);
      } catch (error) {
        console.log(error);
      }
    }, 300),
    [taskEdited]
  );

  const handleChangeTask = (e: any, id: any, prop: string) => {
    const newTasks = tasks.map((task: any) => {
      if (!task.deleted) {
        if (task._id === id) {
          task[prop] = e.target.value;
          setTaskEdited(task);
        }
        return task;
      }
    });
    updateTask();
    dispatch({ type: 'GET_ALL_TASKS', payload: newTasks });
  };

  const handleDeleteTask = async (id: any) => {
    try {
      const option = {
        method: 'DELETE',
        url: '/api/v1/tasks/' + id + '?projectId=' + projectId + '&page=' + paged,
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      };
      const res = await axios(option);
      if (res.data.data.tasks) {
        const newTasks = res.data.data.tasks.filter((task: any) => !task.deleted);
        dispatch({ type: 'GET_ALL_TASKS', payload: newTasks });
        setNumberOfPages(res.data.data.numberOfPages);
      }
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    const tasks = state.user ? state.tasks : [];
  }, [state]);

  const handleCopyExportData = (task: any) => {
    const data = task.exportData.template;
    const builder = task.settings.builder;
    setExportJson(data);

    if (builder === 'webflow') {
      document.execCommand('copy');
    } else {
      if (task.exportData.size <= 1000000 || ['breakdance', 'gutenberg'].includes(builder)) {
        copyJsonToClipboard(task.exportData.template, builder);
      } else {
        if (builder === 'bricks') {
          data.global_classes = data.globalClasses;
          delete data.globalClasses;
        }
        const blob = new Blob([JSON.stringify(data)], { type: 'application/json' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', textToSlug(data.title) + '.json');
        document.body.appendChild(link);
        link.click();
      }
    }
  };

  document.addEventListener("copy", function (event) {
		event.preventDefault();
		event?.clipboardData?.setData("application/json", JSON.stringify(exportJson));
	});

  const textToSlug = (text: string) => {
    return text
      .toLowerCase()
      .replace(/ /g, '-')
      .replace(/[^\w-]+/g, '');
  };

  const copyJsonToClipboard = (data: any, builder: string) => {
		if (data) {
			let jsonString = '';

			switch (builder) {
				case 'elementor':
				case 'elementorFree':

					jsonString = JSON.stringify({ type: 'elementor', siteurl: 'https://elementor.com/wp-json/', elements: data.content }, null, 2);

					break;

				case 'bricks':

					jsonString = JSON.stringify({
						content: data.content,
						source: 'bricksCopiedElements',
						sourceUrl: '',
						version: '1.9.2',
						globalClasses: data.globalClasses,
						globalElements: []
					}, null, 2);

					break;
				case 'breakdance':
					jsonString = JSON.stringify(data, null, 2);
					break;
			}

			const jsonStringMinified = builder === 'gutenberg' ? data : JSON.stringify(JSON.parse(jsonString));

			navigator.clipboard.writeText(jsonStringMinified)
				.then(() => {
          // do something
				})
				.catch((error) => {
					console.error('Error copying JSON data to clipboard:', error);
				});
		}
	};

  return (
    <>
      <div className="breadcrumb">
        <div
          className="breadcrumb__inner"
          onClick={() => {
            navigate('/');
          }}
        >
          <Iconify icon="material-symbols:arrow-back-sharp" />
          <span>Back to Projects</span>
        </div>
      </div>
      <div className="tasks">
        <div
          className="tasks__item"
          onClick={() => {
            setOpenModal(true);
          }}
        >
          <div className="tasks__item-inner">
            <div className="tasks__item-name">
              + Create a new export task 
            </div>
            <div className="tasks__item-description">
              Remaining: {state.tasksRemaining === -1 ? 'unlimited' : state.tasksRemaining}
            </div>
          </div>
        </div>
        {tasks.map((task: any) => (
          !task.deleted && (
            <Task key={task._id} task={task} handleChangeTask={handleChangeTask} setTaskDeleteId={setTaskDeleteId} setTaskDelete={setTaskDelete} setOpenModalConfirm={setOpenModalConfirm} handleCopyExportData={handleCopyExportData} />
          )
        ))}
      </div>
      {numberOfPages > 1 && (
        <div className="pagination">
          <div
            className={paged > 1 && !loading ? "pagination__item pagination__item--prev pagination__item--arrow" : "pagination__item pagination__item--arrow pagination__item--prev pagination__item--disabled"}
            onClick={() => {
              if (paged > 1) {
                navigate(`/projects/${projectId}/${Number(paged) - 1}`);
                setLoading(true);
              }
            }}
          >
            <Iconify icon="akar-icons:chevron-left" />
          </div>
          <div className="pagination__item">{paged}</div>
          <div
            className={numberOfPages > paged && !loading ? "pagination__item pagination__item--next pagination__item--arrow" : "pagination__item pagination__item--arrow pagination__item--next pagination__item--disabled"}
            onClick={() => {
              if (numberOfPages > paged) {
                navigate(`/projects/${projectId}/${Number(paged) + 1}`);
                setLoading(true);
              }
            }}
          >
            <Iconify icon="akar-icons:chevron-right" />
          </div>
        </div>
      )}
      {openModal && (
        <Modal
          modalTitle={'Create a new export task'}
          setOpenModal={setOpenModal}
          openModal={openModal}
          modalContent={<Panel openModal={openModal} setOpenModal={setOpenModal} project={project} />}
        />
      )}
      {openModalConfirm && (
        <Modal
          modalTitle={'Confirm to delete this task'}
          setOpenModal={setOpenModalConfirm}
          openModal={openModalConfirm}
          modalContent={
            <div className="modal__content">
              {taskDelete.status === 'completed' && (
                <div className="login__error">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20px" height="20px" viewBox="0 0 24 24">
                    <path d="M0 0h24v24H0z" fill="none"></path>
                    <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z"></path>
                  </svg>
                  <span>If you delete this export task, it will still be counted as a used export task</span>
                </div>
              )}
              <br />
              <Stack direction="row" spacing={2} color="#3c6df1">
                <Button
                  variant="outlined"
                  size="large"
                  fullWidth
                  onClick={() => {
                    setOpenModalConfirm(false);
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  size="large"
                  fullWidth
                  onClick={() => {
                    handleDeleteTask(taskDeleteId);
                    setOpenModalConfirm(false);
                  }}
                >
                  Delete
                </Button>
              </Stack>
            </div>
          }
        />
      )}
    </>
  );
}

function formatDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0'); // Tháng bắt đầu từ 0 nên cần +1
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}/${month}/${day}`;
}

function capitalizeFirstLetter(string:string) {
  return string.charAt(0).toUpperCase() + string.slice(1);
}

const Task = memo(({ task, handleChangeTask, setTaskDeleteId, setTaskDelete, setOpenModalConfirm, handleCopyExportData }: any) => {
  const [copyText, setCopyText] = useState(task.settings.builder === 'webflow' ? 'Double click' : 'Copy');
  let statusText = capitalizeFirstLetter(task.status);
  if (task.status === 'failed') {
    switch (task.failedReason) {
      case 1:
      case 3:
        statusText = `<a href='https://softlite.io/docs/figwebx/import-from-figma/' target='_blank'>Failed. We can't access the Figma file. Please follow this document carefully or contact us →</a>`;
        break;
      case 2:
        statusText = `<span>Failed. It seems like you haven't selected a layer when copying the link to the selection. Please try again.</span>`;
        break;
      case 4:
        statusText = `<span>Failed. Please contact us or try again.</span>`;
        break;
      case 5:
        statusText = `<span>Failed. Please contact us or try again.</span>`;
        break;
      case 6:
        statusText = `<span>Failed. You're selecting a section. Please select a frame and try again.</span>`;
        break;
      default:
        statusText = `<span>Failed. Please contact us or try again.</span>`;
        break;
    }
  }

  return (
    <div
      className="tasks__item"
      data-key={task._id}
      key={task._id}
    >
      <div
        className="tasks__item-inner"
      >
        <div className="tasks__item-name">
          <input type="text" value={task.name} onChange={(e) => handleChangeTask(e, task._id, 'name')} />
        </div>
        <div className="tasks__item-tools">
          <div className="tasks__item-tool">
            <Tooltip title="Delete" arrow>
              <IconButton
                onClick={() => {
                  setTaskDeleteId(task._id);
                  setTaskDelete(task);
                  setOpenModalConfirm(true);
                }}
              >
                <Iconify icon="ph:trash" />
              </IconButton>
            </Tooltip>
          </div>
        </div>
        <div
          className={"tasks__item-status tasks__item-status--" + task.status}
          data-failed-reason={task.failedReason}
          dangerouslySetInnerHTML={{ __html: statusText }}
        >
        </div>
      </div>
      <div className="tasks__item-footer">
        <div className="tasks__item-footer-left">
          <div className="tasks__item-builder">
            {task.settings.builder === 'elementorFree' ? 'elementor' : task.settings.builder}
          </div>
          <div className="tasks__item-date">{ formatDate(new Date(task.createdAt)) }</div>
        </div>
        {task.status === 'completed' && (
          <div className="tasks__item-button">
            <Button variant="contained" onClick={() => {
              handleCopyExportData(task);
              setCopyText('Copied');
              setTimeout(() => {
                setCopyText(task.settings.builder === 'webflow' ? 'Double click' : 'Copy');
              }, 3000);
            }}>{task.exportData  && task.exportData.size > 1000000 && !['breakdance', 'gutenberg','webflow'].includes(task.settings.builder) ? "Download" : copyText}</Button>
          </div>
        )}
      </div>
    </div>
  );
});

export default Tasks;
